// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
// ── Risk tiers ───────────────────────────────────────────────────
/**
 * Decree 142's three tiers, quoted from `vn-ai142-r-classify-before-use`:
 *
 * > "Providers must classify each AI system as low-risk, medium-risk, or
 * > high-risk before putting the system into service."
 *
 * This union *is* hardcoded, and legitimately so: the tiers are the operative
 * vocabulary of the decree's obligations, not a list the decree delegates to an
 * amendable appendix. The amendable part — which systems land in which tier —
 * is the pack's.
 */
/**
 * fix-sprint-20260808-2111 (finding #31) — THE reader of "does this category
 * (or this stored match) start the high-risk obligation track".
 *
 * The neutral `starts_obligation_track` wins; the deprecated field is read
 * only as a legacy fallback, for a run persisted (or a list installed) before
 * the platform stopped writing it. Neither present reads `false`.
 */
export function startsObligationTrack(record) {
    if (typeof record.starts_obligation_track === 'boolean')
        return record.starts_obligation_track;
    return record.triggers_decree_142_high_risk_workflows === true;
}
export const AI_RISK_TIERS = ['low_risk', 'medium_risk', 'high_risk'];
/**
 * Ordering for "is this an escalation?" comparisons. Higher is riskier.
 *
 * Sprint 64.7.0 — this remains the **platform default**, and is no longer the
 * only ladder. `AiTierLadder` below carries a scheme's own declared ordering,
 * and `obligationApplies` takes one rather than reaching for this constant.
 *
 * Kept, rather than deleted, for three reasons. It is the correct ladder for a
 * Vietnam tenant, so nothing moves for one (AC1). It is the fallback for an
 * organisation whose installed AI framework declares no tiers, which is a real
 * state rather than an error. And the three values are persisted on every
 * `grc_ai_classification_runs` row, so they stay valid readings whatever a
 * second regulator's scheme calls its own tiers.
 */
export const AI_RISK_TIER_RANK = {
    low_risk: 0,
    medium_risk: 1,
    high_risk: 2,
};
/** The platform default ladder, as an `AiTierLadder`. */
export const DEFAULT_AI_TIER_LADDER = AI_RISK_TIER_RANK;
// ── AI system profile ────────────────────────────────────────────
export const AI_SYSTEM_LIFECYCLE_STAGES = [
    'design',
    'development',
    'testing',
    'sandbox',
    'production',
    'retired',
];
export const AI_SYSTEM_PROVIDER_TYPES = ['in_house', 'third_party', 'hybrid'];
export const AI_SYSTEM_DEPLOYMENT_CONTEXTS = [
    'internal_only',
    'customer_facing',
    'public',
    'restricted_pilot',
];
/**
 * How much the system decides without a person in the loop.
 *
 * Drawn from `vn-ai142-r-classify-before-use`'s "autonomy level" input. The
 * three values are the ones the decree's human-oversight obligation
 * distinguishes between: advisory output, a decision a person confirms, and a
 * decision taken without confirmation.
 */
export const AI_AUTONOMY_LEVELS = [
    'advisory_only',
    'human_in_the_loop',
    'human_on_the_loop',
    'fully_automated',
];
export const AI_SYSTEM_PROFILE_STATUSES = ['active', 'archived'];
/**
 * fix-sprint-20261031-1800 item 2 — where a profile's `purpose` came from (`AiSystemProfile.purpose_source`).
 */
export const AI_SYSTEM_PURPOSE_SOURCES = ['recorded', 'copied_from_description'];
// ── Reclassification signal (Sprint 62.26.0, AC13/AC14) ──────────
/**
 * The six classification inputs, as a value — Sprint 62.26.0.
 *
 * The invalidation comparison reads this list, so it is proved EXACT in both
 * directions below rather than trusted: a field added to
 * `AiClassificationInputs` and not listed here would be a change the signal
 * could never see, and `satisfies` alone accepts a list missing a member (the
 * `TASK_STATUSES` contract).
 */
export const AI_CLASSIFICATION_INPUT_FIELDS = [
    'intended_use',
    'data_categories',
    'sector',
    'user_group',
    'autonomy_level',
    'potential_harm',
];
/** Every listed member is an input — the direction `satisfies` gives. */
const _inputFieldsAreInputs = AI_CLASSIFICATION_INPUT_FIELDS;
/** …and every input is listed — the direction it does not. */
const _inputFieldsAreComplete = null;
void _inputFieldsAreInputs;
void _inputFieldsAreComplete;
export const AI_RECLASSIFICATION_COMPARED_FIELDS = [
    ...AI_CLASSIFICATION_INPUT_FIELDS.map((f) => `classification_inputs.${f}`),
    'lifecycle_stage',
];
export const AI_RECLASSIFICATION_SIGNAL_STATUSES = ['open', 'resolved'];
/**
 * Why a signal stopped being open. Neither is a decision about the tier:
 *
 * - `inputs_match_approved_run` — a later edit made the compared fields equal
 *   to the approved run's snapshot again, so the statement the signal makes
 *   ("these differ") is no longer true. The approval was never touched.
 * - `later_approval` — a person approved a later classification run, so the
 *   run the signal was about is no longer the one in force.
 */
export const AI_RECLASSIFICATION_RESOLUTIONS = [
    'inputs_match_approved_run',
    'later_approval',
];
export const AI_RECLASSIFICATION_SIGNAL_COLLECTION = 'grc_ai_reclassification_signals';
export const AI_CLASSIFICATION_REVIEW_STATUSES = [
    'pending_review',
    'approved',
    'rejected',
    'superseded',
];
/**
 * Why a run stopped being the current one — Sprint 64.38.0.
 *
 * The mirror of `CLASSIFICATION_SUPERSEDE_REASONS` on the assessment model, and
 * added for the reason that one was: `superseded_by_run_id` said *which* run
 * replaced this one and nothing said *what happened*. Until this sprint there
 * was only one way to reach `superseded` — a later run was approved — so the
 * pointer carried the whole story. There are now two, and they are not the same
 * fact about the record:
 *
 * - **`later_approval`** — a person made a later decision, and every earlier
 *   approval stops claiming currency. This is what `reviewRun` writes.
 * - **`duplicate_pending_resolution`** — nobody decided anything. Two runs for
 *   one profile were left `pending_review` by the non-atomic read-then-write
 *   `createRun` used before its partial unique index, and Sprint 64.38.0's
 *   migration resolved the group so the index could build. The record's own
 *   review history is untouched; only its currency changed, and no reviewer is
 *   named because none existed.
 *
 * An auditor reading a register of superseded proposals must be able to tell a
 * human decision from a data repair. The pointer alone cannot.
 */
export const AI_CLASSIFICATION_SUPERSEDE_REASONS = [
    'later_approval',
    'duplicate_pending_resolution',
];
// ── Obligation records ───────────────────────────────────────────
/**
 * AC4's six independently statused obligation kinds.
 *
 * These do **not** all come from Decree 142. Measured at kickoff: human
 * oversight has no standalone requirement in Decree 142's sixteen — it is
 * `f523fbc2-…` in `VN_AI_LAW_2026` and `vn-aid33-r-human-oversight` in Decision
 * 33. Instantiating from Decree 142 alone would satisfy AC3's wording and leave
 * AC4 unsatisfiable, so instantiation reads the installed AI framework set.
 */
export const AI_OBLIGATION_KINDS = [
    'transparency_labeling',
    'human_oversight',
    'conformity',
    'monitoring',
    'incident',
    'sandbox',
];
/**
 * Where an obligation's tier floor came from — Sprint 64.7.0, AC5.
 *
 * ## The defect this replaces
 *
 * `resolveObligation` used to fall back to `applies_from_tier: 'low_risk'` for
 * any requirement the platform catalogue did not declare. Combined with
 * `obligationApplies`'s tier **floor**, that meant an uncatalogued obligation
 * applied to **every** system at every tier. So installing an AI framework the
 * module had never seen produced neither an error nor an empty panel: it
 * produced a full obligation list in which a high-risk-only duty was attached
 * to a minimal-risk system, each row carrying `declared: false` and nothing on
 * screen saying so.
 *
 * That is worse than failing, because a compliance officer reading the panel
 * cannot tell a catalogued obligation from a guessed one. The three bases below
 * are three different things to tell them.
 */
export const AI_OBLIGATION_SCOPE_BASES = [
    /** The pack's own requirement declares the tier it starts applying at. */
    'pack_declared',
    /** The platform's obligation catalogue declares it. A platform judgement. */
    'platform_catalogue',
    /**
     * Neither. The obligation is **unscoped**: it is instantiated so the duty is
     * not silently dropped, and it is presented as unscoped rather than as
     * applying to everything.
     */
    'undeclared',
];
/**
 * Obligation status.
 *
 * `not_started` is the state every record is instantiated in — AC3 requires
 * instantiation "without auto-approving compliance", so no code path may create
 * a record in any other state. `not_applicable` requires a rationale; it is a
 * recorded decision, not a way to make a row disappear.
 */
export const AI_OBLIGATION_STATUSES = [
    'not_started',
    'in_progress',
    'evidence_captured',
    'complete',
    'not_applicable',
];
// ── Governance evidence fields (Sprint 62.29.0) ───────────────────
/**
 * The five evidence areas an AI system can carry, each discovered from an
 * installed framework's `ai_governance_evidence_fields` declaration.
 *
 * The vocabulary is the platform's (these are record shapes), and which
 * instrument gives each a basis is the pack's. A tenant whose installed
 * frameworks declare none of them sees none of them.
 */
export const AI_GOVERNANCE_EVIDENCE_FIELDS = [
    'ethics_review',
    'conformity_status',
    'model_evaluation',
    'ai_security_testing',
    'sector_criteria',
];
/**
 * `available` offers the field and raises nothing. `required` is reserved for
 * a signed instrument that requires the evidence of the organisation holding
 * it; the corpus declares none today, and a test holds that set empty.
 */
export const AI_GOVERNANCE_EVIDENCE_ACTIVATIONS = ['available', 'required'];
/**
 * Ethics review states — the vocabulary of the self-assessment template the
 * declaring instrument publishes (Met / Not met / Not applicable), plus the
 * state of an entry nobody has recorded.
 */
export const AI_ETHICS_REVIEW_STATES = [
    'not_recorded',
    'met',
    'not_met',
    'not_applicable',
];
/**
 * Conformity status states. None of them is "certified" or "approved": an
 * outcome issued by an authority is recorded as `authority_decision_recorded`
 * with the issuing body and the decision reference, and the platform never
 * turns an internal or third-party assessment into a certification.
 */
export const AI_CONFORMITY_STATUS_STATES = [
    'not_assessed',
    'internal_assessment_recorded',
    'third_party_assessment_recorded',
    'authority_decision_recorded',
];
/** States for model evaluation, AI-security testing and sector criteria. */
export const AI_EVIDENCE_RECORD_STATES = ['not_recorded', 'recorded'];
/** The state vocabulary each field takes. One function, used by server and client. */
export function aiGovernanceEvidenceStates(field) {
    if (field === 'ethics_review')
        return AI_ETHICS_REVIEW_STATES;
    if (field === 'conformity_status')
        return AI_CONFORMITY_STATUS_STATES;
    return AI_EVIDENCE_RECORD_STATES;
}
/**
 * EN / VI labels for the fields and states, defined once so the register page
 * and the evidence-pack titles a regulator reads cannot drift apart. No label
 * says "certified", "compliant" or "approved" (AC4): the ethics review uses the
 * self-assessment template's own words (Đạt / Chưa đạt / Chưa áp dụng), and an
 * authority outcome is "recorded", never asserted.
 */
export const AI_GOVERNANCE_EVIDENCE_FIELD_LABELS = {
    ethics_review: { en: 'Ethics review', vi: 'Đánh giá đạo đức' },
    conformity_status: { en: 'Conformity status', vi: 'Tình trạng đánh giá sự phù hợp' },
    model_evaluation: { en: 'Model evaluation', vi: 'Đánh giá mô hình' },
    ai_security_testing: { en: 'AI security testing', vi: 'Kiểm thử an ninh hệ thống AI' },
    sector_criteria: { en: 'Sector criteria', vi: 'Tiêu chí theo ngành, lĩnh vực' },
};
export const AI_GOVERNANCE_EVIDENCE_STATE_LABELS = {
    not_recorded: { en: 'Not recorded', vi: 'Chưa ghi nhận' },
    met: { en: 'Met', vi: 'Đạt' },
    not_met: { en: 'Not met', vi: 'Chưa đạt' },
    not_applicable: { en: 'Not applicable', vi: 'Chưa áp dụng' },
    not_assessed: { en: 'Not assessed', vi: 'Chưa đánh giá' },
    internal_assessment_recorded: {
        en: 'Internal assessment recorded',
        vi: 'Đã ghi nhận đánh giá nội bộ',
    },
    third_party_assessment_recorded: {
        en: 'Third-party assessment recorded',
        vi: 'Đã ghi nhận đánh giá của bên thứ ba',
    },
    authority_decision_recorded: {
        en: 'Authority decision recorded',
        vi: 'Đã ghi nhận quyết định của cơ quan có thẩm quyền',
    },
    recorded: { en: 'Recorded', vi: 'Đã ghi nhận' },
};
/** The state a field reads as when nothing has been recorded. */
export function aiGovernanceEvidenceUnrecordedState(field) {
    return field === 'conformity_status' ? 'not_assessed' : 'not_recorded';
}
// ── Screening ruleset ────────────────────────────────────────────
/**
 * Version of the **platform-authored screening logic** that turns declared
 * category matches and the six Decree 142 inputs into a *proposed* tier.
 *
 * Recorded on every run and deliberately separate from `pack_version`, because
 * the two change for different reasons and AC8's "rule-version repeatability"
 * has to survive both. A pack upgrade changes *which systems are listed*; a
 * bump here changes *how a proposal is derived from a declaration*.
 *
 * Be precise about what this is. Which categories exist, and the fact that a
 * match carries `high_risk`, are the regulation's — read from the pack. The
 * medium-risk rule below is **not** quoted from any instrument: it is a
 * screening heuristic this platform authors, over the inputs
 * `vn-ai142-r-classify-before-use` names. That is why its output is a
 * proposal with a rationale and never a classification, and why the rationale
 * always names the rule that fired.
 */
export const AI_SCREENING_RULESET_VERSION = '62.3.0';
/** Identifiers for the screening rules, so a rationale can name what fired. */
export const AI_SCREENING_RULE_IDS = [
    /**
     * One or more declared categories from the resolved high-risk list matched.
     * Source: the pack.
     *
     * Sprint 64.7.0, AC6 decision — recorded here rather than settled silently.
     * The value written **going forward** is this one. `decision_33_category_match`
     * below is retained as a readable legacy value and is never written again.
     *
     * Why not simply rename: `grc_ai_classification_runs` rows are immutable
     * audit records a regulator may be shown, and rewriting them is out of scope
     * by the spec's own risk section. Why not simply keep it: it named a
     * Vietnamese instrument on **new** records for tenants that have never
     * installed one — preserving old rows is a reason to keep the value readable,
     * not a reason to keep writing it.
     */
    'declared_category_match',
    /** @deprecated Sprint 64.7.0 — readable, never written. See above. */
    'decision_33_category_match',
    /** No listed match, but the system's inputs meet the medium-risk heuristic. */
    'medium_risk_input_heuristic',
    /** Neither of the above. */
    'no_elevating_factor',
];
// ── Collections ──────────────────────────────────────────────────
export const AI_SYSTEM_PROFILE_COLLECTION = 'grc_ai_system_profiles';
export const AI_CLASSIFICATION_RUN_COLLECTION = 'grc_ai_classification_runs';
export const AI_OBLIGATION_RECORD_COLLECTION = 'grc_ai_obligation_records';
export const AI_SYSTEM_VERSION_COLLECTION = 'grc_ai_system_versions';
export const AI_VERSION_CHANGE_COLLECTION = 'grc_ai_version_changes';
export const AI_ROLLBACK_EVENT_COLLECTION = 'grc_ai_rollback_events';
export const AI_SECURITY_TEST_COLLECTION = 'grc_ai_security_tests';
export const AI_VALIDATION_COLLECTION = 'grc_ai_validations';
export const AI_INVENTORY_REVIEW_COLLECTION = 'grc_ai_inventory_reviews';
export const AI_IMPACT_ASSESSMENT_COLLECTION = 'grc_ai_impact_assessments';
export const AI_RELEASE_APPROVAL_COLLECTION = 'grc_ai_release_approvals';
// ── Versions and change control (Sprint 62.48.0, AC6) ────────────
/**
 * `baseline` is the version a system held when its history began; `change` is a
 * version a later edit or explicit record created; `rollback` is a version that
 * restores an earlier one as a new row, so the history never loses a value.
 */
export const AI_VERSION_KINDS = ['baseline', 'change', 'rollback'];
/**
 * A re-validation decision. Either way it is a decision, by a named person, with
 * a reason: "not required" is a reasoned answer, not an absence.
 */
export const AI_REVALIDATION_DECISIONS = [
    'revalidation_required',
    'revalidation_not_required',
];
// ── Security testing by attack category (Sprint 62.48.0, AC8) ────
/**
 * The attack categories of Điều 6 khoản 1 a). One record per category per
 * version. Data poisoning, model poisoning, evasion and model extraction are the
 * four a high-risk version may not release with open (AC7).
 */
export const AI_ATTACK_CATEGORIES = [
    'privacy_breach',
    'data_poisoning',
    'model_poisoning',
    'evasion',
    'model_extraction',
    'prompt_injection',
    'hallucination',
    'bias_in_generated_output',
];
/**
 * `not_mandatory` (Sprint 62.48.0, cluster B, G9) is a recorded answer for a
 * non-high-risk version: a poisoning, evasion or extraction category the test
 * plan does not have to run. It carries a reason, and only for the categories in
 * `AI_HIGH_RISK_MANDATORY_ATTACK_CATEGORIES`.
 */
export const AI_SECURITY_TEST_STATES = [
    'performed',
    'not_applicable',
    'open',
    'not_mandatory',
];
/**
 * The categories a high-risk version may not release with open (AC7, Điều 6
 * khoản 1 a)). For any other version they may be recorded `not_mandatory`.
 */
export const AI_HIGH_RISK_MANDATORY_ATTACK_CATEGORIES = [
    'data_poisoning',
    'model_poisoning',
    'evasion',
    'model_extraction',
];
/**
 * `not_scheduled` is a version with no schedule, and is never read as compliant.
 * `scheduled` is before the next test date; `due` is on it; `overdue` is after it.
 */
export const AI_PERIODIC_TEST_STATES = ['not_scheduled', 'scheduled', 'due', 'overdue'];
/**
 * `required` (the default) refuses a re-test by the person who fixed a finding.
 * `not_required` must cite the policy that says so.
 */
export const AI_SEPARATION_POLICIES = ['required', 'not_required'];
// ── Independent model validation (Sprint 62.48.0, AC9) ───────────
/**
 * The test areas of Điều 16 khoản 3: the in-house tests (a, the six areas the text
 * names) and the third-party tests (b, the five the text names, read from the
 * pack's requirement for that khoản).
 */
export const AI_VALIDATION_TEST_AREAS = [
    'data_fit',
    'algorithm_soundness',
    'performance_accuracy',
    'explainability',
    'fairness_bias_control',
    'stability',
    'third_party_purpose_scope_risk',
    'third_party_black_box_on_own_data',
    'third_party_output_under_operating_conditions',
    'third_party_provider_documentation',
    'third_party_human_controls_and_monitoring',
];
// ── Register core: inventory, banking triggers, impact, release (Sprint 62.48.0, wave 1B) ──
/**
 * The measures a banking high-risk trigger is stated in (Điều 4 khoản 2). Each
 * is a fact the institution records; the thresholds are the pack's, declared on
 * the framework record as `ai_banking_thresholds`, never a number in `src/`.
 */
export const AI_BANKING_THRESHOLD_MEASURES = [
    'per_transaction_payment',
    'daily_total_per_customer',
    'credit_amount',
];
/** A threshold applies to one customer class, or to any (the credit trigger). */
export const AI_BANKING_THRESHOLD_CUSTOMER_CLASSES = ['individual', 'organisation', 'any'];
/** A trigger fact records a customer class; `any` is a threshold's reach, not a fact's. */
export const AI_BANKING_FACT_CUSTOMER_CLASSES = ['individual', 'organisation'];
export const AI_BANKING_THRESHOLD_COMPARISONS = ['greater_than', 'greater_or_equal'];
/** Fix sprint #602 — a person's answer to whether a system is on the official high-risk list. */
export const AI_HIGH_RISK_LIST_STATUSES = ['listed', 'not_listed'];
/** The precondition kinds a framework may declare for its banking triggers (`ai_banking_trigger_precondition`). */
export const AI_BANKING_TRIGGER_PRECONDITION_KINDS = ['system_on_high_risk_list'];
/**
 * Whether the banking triggers reach a system: `applies` (the system is recorded on the list, or no installed
 * framework declares a precondition), `not_applicable` (recorded as not on the list), `not_recorded` (no answer), or
 * `precondition_unreadable` (an installed framework declares a precondition the platform cannot read; the triggers then
 * reach no system, and the page says why).
 */
export const AI_BANKING_TRIGGER_SCOPE_STATUSES = [
    'applies',
    'not_applicable',
    'not_recorded',
    'precondition_unreadable',
];
// ── Annual inventory review (AC2; Điều 4 khoản 5) ────────────────
/** The deadline record that carries the review cadence declares this surface. */
export const AI_INVENTORY_REVIEW_REGISTER_SURFACE = 'ai_inventory_review';
/**
 * fix-sprint-20261031-1200 F1 — the deadline record of the periodic report to the senior management body declares
 * this surface: its interval, the tiers it covers, the event that owes its own item and the contents it carries are
 * read from that record, never written here (R-46).
 */
export const AI_BOARD_REPORT_REGISTER_SURFACE = 'ai_board_report';
export const AI_INVENTORY_REVIEW_OUTCOMES = ['confirmed', 'changed'];
export const AI_INVENTORY_REVIEW_READINGS = [
    'confirmed',
    'open',
    'overdue',
    'cadence_not_declared',
    // fix-sprint-20261031-1200 F34 — retired within the period: no due date, never overdue.
    'retired',
];
// ── Release gate (AC7; Điều 12 khoản 3; Điều 22 khoản 1–2) ────────
/**
 * One precondition a release can be missing. A refusal names each missing
 * precondition once, so a reviewer reads the whole list and not the first gap.
 */
export const AI_RELEASE_MISSING_ITEMS = [
    'risk_tier',
    'impact_assessment',
    'validation',
    'validation_findings',
    'security_tests',
    'rollback_plan',
    'senior_management_approval',
];
export const AI_RELEASE_APPROVAL_STATUSES = ['current', 'stale'];
/**
 * How a dossier labels an approval against the go-live date it names. An
 * approval recorded after that date is after-the-fact, and says so.
 */
export const AI_DOSSIER_APPROVAL_LABELS = ['before_go_live', 'after_the_fact'];
// ── Error codes ──────────────────────────────────────────────────
/**
 * Every code this module throws. Held here so the frontend can branch on a
 * code rather than parse a message, and so a reachability test can assert each
 * one is actually thrown — a declared code nothing raises is a contract nobody
 * can rely on.
 */
export const AI_GOVERNANCE_ERROR_CODES = {
    PROFILE_NOT_FOUND: 'ai_system_profile_not_found',
    RUN_NOT_FOUND: 'ai_classification_run_not_found',
    OBLIGATION_NOT_FOUND: 'ai_obligation_record_not_found',
    /** The Decision 33 pack is not installed for this organisation. */
    RULE_SOURCE_NOT_INSTALLED: 'ai_decision_33_not_installed',
    /**
     * More than one installed framework publishes an AI high-risk list and the
     * request named none — fix-sprint-20260808-2111-followup-followup F3.
     *
     * Distinct from `RULE_SOURCE_NOT_INSTALLED` because the remedies are
     * opposites: that one means install something, this one means choose. The
     * payload carries `eligible_framework_codes` so the client can offer exactly
     * the choice the server will accept.
     *
     * Refusing is the point. Screening resolved `docs.find(declaresScheme) ??
     * docs.find(carriesLegacyList)` over a code-point sort, so which regulator's
     * list a system was measured against was decided by string ordering and then
     * written to an immutable run.
     */
    FRAMEWORK_SELECTION_REQUIRED: 'ai_framework_selection_required',
    /**
     * The named framework does not publish an AI high-risk list for this
     * organisation — not installed, or its scheme was refused by
     * `ClassificationSchemeRegistry` (see `diagnostics` on the payload).
     */
    UNKNOWN_FRAMEWORK: 'ai_unknown_framework',
    /** A declared category code is not on the installed list. */
    UNKNOWN_CATEGORY: 'ai_decision_33_unknown_category',
    /**
     * `classification_inputs.sector` is not one of the sectors on the installed
     * list.
     *
     * Fix sprint F15. `AiClassificationInputs.sector` says it is "validated
     * against the sectors present on the installed framework" and nothing did:
     * the route accepted any string up to 120 characters, and the screening
     * evaluator counts a non-null sector as an elevating factor. `sector:
     * 'banana'` produced the persisted, regulator-readable rationale `operates in
     * the listed sector "banana"` — a false statement of fact on a compliance
     * record — and, with one further factor, proposed medium risk on a value no
     * instrument contains.
     *
     * Only raised when a list is installed to check against. A tenant without the
     * pack has no list to be wrong about.
     */
    UNKNOWN_SECTOR: 'ai_decision_33_unknown_sector',
    /**
     * The reviewer's `final_tier` is not a tier the resolving scheme declares.
     *
     * Sprint 64.7.0, gate E12.5 P0-2. This replaces `z.enum(AI_RISK_TIERS)` on
     * the route: the constraint is the regulator's own tier list, not the
     * platform's three, so it is checked where the scheme is known.
     */
    UNKNOWN_TIER: 'ai_unknown_risk_tier',
    /** A `linked_framework_codes` entry names a framework the org has not installed. */
    FRAMEWORK_NOT_INSTALLED: 'ai_governance_framework_not_installed',
    /** `linked_system_id` names a `grc_systems` record that does not exist. */
    LINKED_SYSTEM_NOT_FOUND: 'ai_governance_linked_system_not_found',
    /** A review was attempted on a run that is not `pending_review`. */
    RUN_NOT_PENDING_REVIEW: 'ai_classification_run_not_pending_review',
    /** Sprint 62.48.0, wave 4 (AC14) — the run's creator may not review it (403). */
    REVIEWER_IS_RUN_CREATOR: 'ai_classification_reviewer_is_creator',
    /** Sprint 62.48.0, wave 4 (AC14) — a framework must cover every declared risk type (422). */
    RISK_FRAMEWORK_INCOMPLETE: 'ai_risk_framework_incomplete',
    /** fix-sprint-20261018-2116 F9 — only a risk type limited to some addressees may be marked not applicable (422). */
    RISK_TYPE_NOT_APPLICABLE_REFUSED: 'ai_risk_type_not_applicable_refused',
    /** Sprint 62.48.0, wave 4 (AC14) — the pack declares no risk types to cover (422). */
    RISK_TYPES_NOT_DECLARED: 'ai_risk_types_not_declared',
    /** Sprint 62.48.0, wave 4 (AC14) — a named person must be a user of the organisation (400). */
    APPROVER_NOT_RESOLVED: 'ai_approver_not_resolved',
    /** Sprint 62.48.0, wave 4 (AC14) — a line's named holder must resolve in the organisation (400). */
    THREE_LINE_HOLDER_NOT_RESOLVED: 'ai_three_line_holder_not_resolved',
    /** Sprint 62.48.0, wave 4 (AC14) — the second line names a holder as P-VN-42 or P-VN-07 (400). */
    THREE_LINE_SECOND_PERSONA_INVALID: 'ai_three_line_second_persona_invalid',
    /** Sprint 62.48.0, wave 4 (AC14) — a map names each of the three lines exactly once (400). */
    THREE_LINES_MAP_INCOMPLETE: 'ai_three_lines_map_incomplete',
    /** Fix-sprint-20261031-1200 F6 — a line is attested only by a person named as one of its holders (403). */
    THREE_LINE_ATTESTER_NOT_HOLDER: 'ai_three_line_attester_not_holder',
    /** Fix-sprint-20261031-1200 F6 — an attestation names the current map version; an older one is refused (409). */
    THREE_LINES_MAP_SUPERSEDED: 'ai_three_lines_map_superseded',
    /** Fix-sprint-20261031-1200 F6 — the map the attestation names is not this organisation's (404). */
    THREE_LINES_MAP_NOT_FOUND: 'ai_three_lines_map_not_found',
    /**
     * A screening was started while one is already awaiting review.
     *
     * Gate E12.5 found that two concurrently pending runs let a reviewer approve
     * the older one *after* the newer, silently replacing a high-risk
     * classification with a stale low-risk proposal — and leaving both runs
     * claiming to be current. One pending screening at a time is the guarantee
     * that makes review order irrelevant.
     */
    RUN_ALREADY_PENDING: 'ai_classification_run_already_pending',
    /**
     * A review was attempted on a run older than one already approved.
     *
     * The second half of the same guard, kept because a run can become stale
     * between being fetched and being decided.
     */
    RUN_SUPERSEDED_BY_LATER_APPROVAL: 'ai_classification_run_superseded',
    /** `owner_user_id` names a user outside this organisation. */
    OWNER_NOT_FOUND: 'ai_governance_owner_not_found',
    /** A reviewer changed the tier without giving a rationale. */
    REVIEW_RATIONALE_REQUIRED: 'ai_classification_review_rationale_required',
    /** `not_applicable` was set without a rationale. */
    OBLIGATION_RATIONALE_REQUIRED: 'ai_obligation_status_rationale_required',
    /**
     * An obligation was marked `complete`, or its evidence removed while complete,
     * with nothing behind it: no attachment, and no governance evidence field this
     * system has recorded. Sprint 62.48.0, row 21 and AC8. The 422 carries
     * `missing_items`, which names both limbs the gate accepts.
     */
    OBLIGATION_EVIDENCE_REQUIRED: 'ai_obligation_evidence_required',
    /**
     * A date this module cannot order.
     *
     * Fix sprint F17. `due_date` was a free string up to 40 characters — `"soon"`,
     * `"Q3"`, `"31/12/2027"` all stored — and it backs the reporting index
     * `{ org_id: 1, status: 1, due_date: 1 }`, which mongo orders
     * lexicographically. An unparseable value does not fail; it lands at an
     * arbitrary position in a queue of statutory deadlines. Sprint 62.4.0 fixed
     * exactly this for `next_review_at`/`due_date` in the classification register
     * and the AI module was not back-fixed.
     */
    INVALID_DATE: 'ai_governance_invalid_date',
    /**
     * An `evidence_attachments` entry names no attachment in this organisation.
     *
     * PR #2475 re-shaped `UpdateObligationBody.evidence_attachments` to the real
     * `AttachmentRef` and said it mirrored the evidence-pack module exactly. It
     * mirrored the zod shape and not the **resolution**: the refs went straight
     * into `$set`, so an `ai_governance:write` holder could persist a fully
     * client-authored ref — arbitrary `file_id`, `storage_path`, `filename`,
     * `size`, and file ids belonging to another organisation — onto a
     * regulator-readable obligation record. Presence is not resolution; the
     * named precedent
     * (`framework-evidence-pack-dispatch-service.ts`) resolves every ref through
     * `GenericAttachmentService.getByFileId(orgId, …)` and rebuilds the stored
     * ref from the document.
     */
    EVIDENCE_ATTACHMENT_NOT_FOUND: 'ai_governance_evidence_attachment_not_found',
    /** Export was requested for a profile with no approved classification. */
    EXPORT_REQUIRES_APPROVED_CLASSIFICATION: 'ai_governance_export_requires_approved_classification',
    /**
     * Sprint 62.29.0 — the field is not declared by any installed framework, or
     * the named framework does not declare it. The register offers a field only
     * where an installed instrument gives it a basis; the API holds the same line.
     */
    EVIDENCE_FIELD_NOT_DECLARED: 'ai_governance_evidence_field_not_declared',
    /** More than one installed framework declares the field and the request named none. */
    EVIDENCE_FRAMEWORK_SELECTION_REQUIRED: 'ai_governance_evidence_framework_selection_required',
    /** The state is not in the field's vocabulary. */
    EVIDENCE_STATE_INVALID: 'ai_governance_evidence_state_invalid',
    /** An ethics review marked `not_met` without a corrective action, owner and due date. */
    EVIDENCE_CORRECTIVE_ACTION_REQUIRED: 'ai_governance_evidence_corrective_action_required',
    /**
     * `authority_decision_recorded` without the issuing body and decision
     * reference. An authority outcome the record cannot identify is the
     * certification claim this module refuses to make.
     */
    EVIDENCE_AUTHORITY_DECISION_REQUIRED: 'ai_governance_evidence_authority_decision_required',
    /** The linked form record is not in this organisation or is not of a declared schema. */
    EVIDENCE_FORM_INSTANCE_NOT_FOUND: 'ai_governance_evidence_form_instance_not_found',
    /**
     * The linked form record exists but its content is not final
     * (`formContentIsFinal`): a draft or submitted record is not the completed
     * record an evidence entry may rest on.
     */
    EVIDENCE_FORM_INSTANCE_NOT_FINAL: 'ai_governance_evidence_form_instance_not_final',
    /** A review was requested on a field with nothing recorded. */
    EVIDENCE_NOT_RECORDED: 'ai_governance_evidence_not_recorded',
    /**
     * The entry the reviewer confirmed was re-recorded (or removed) between the
     * read and the write. The review is refused rather than landing on an entry
     * the reviewer never saw.
     */
    EVIDENCE_CHANGED_SINCE_READ: 'ai_governance_evidence_changed_since_read',
    /**
     * Sprint 62.26.0, gate E12.5 pass 1 (P2-5) — the profile's `updated_at` is
     * not the `expected_updated_at` the edit was opened on: someone saved it
     * since. Refused (409) with nothing written and no reclassification signal
     * raised, updated or resolved — a stale form sends `classification_inputs`
     * whole, so accepting it would revert the other person's inputs and could
     * resolve the signal their change raised and withdraw its review duty.
     */
    PROFILE_CHANGED_SINCE_READ: 'ai_system_profile_changed_since_read',
    // ── Sprint 62.48.0, wave 1A ──
    /** AC6 — a change after release has no re-validation decision by a named person with a reason. */
    VERSION_CHANGE_REVALIDATION_REQUIRED: 'ai_version_change_revalidation_decision_required',
    VERSION_NOT_FOUND: 'ai_system_version_not_found',
    /** AC6 — a rollback event with no reason. */
    ROLLBACK_REASON_REQUIRED: 'ai_rollback_reason_required',
    /** AC6 — a rollback plan or event names a version that is not this system's, or is the current one. */
    ROLLBACK_TARGET_INVALID: 'ai_rollback_target_invalid',
    ROLLBACK_INCIDENT_NOT_FOUND: 'ai_rollback_incident_not_found',
    /** AC6 — a rollback plan with no steps. */
    ROLLBACK_PLAN_INCOMPLETE: 'ai_rollback_plan_incomplete',
    /** AC6 — a change record with no statement of what changed. */
    VERSION_CHANGE_SUMMARY_REQUIRED: 'ai_version_change_summary_required',
    /** fix-sprint-20261031-1200 F1 — a significance answer (true or false) with no basis. */
    VERSION_SIGNIFICANCE_BASIS_REQUIRED: 'ai_version_significance_basis_required',
    /** Gate E12.5 CR-P2-3 — the change record named is not this system's. */
    VERSION_CHANGE_NOT_FOUND: 'ai_version_change_not_found',
    /**
     * Gate E12.5 CR-P2-3 — the change already carries a significance answer. An answer is recorded once, as the change
     * itself is (F1: entered with the change, never overwritten); a later answer is refused (409), not a revision.
     */
    VERSION_SIGNIFICANCE_ALREADY_RECORDED: 'ai_version_significance_already_recorded',
    /** AC8 — `ai_security_testing` set to recorded while a category has no record. Returned as 422. */
    SECURITY_CATEGORIES_BLANK: 'ai_security_testing_categories_blank',
    SECURITY_TEST_NOT_FOUND: 'ai_security_test_not_found',
    /** AC8 — a second record for a category of the same version. */
    SECURITY_TEST_EXISTS: 'ai_security_test_exists',
    /** AC8 — a record missing a field its state requires. */
    SECURITY_TEST_FIELDS_REQUIRED: 'ai_security_test_fields_required',
    /** AC8 — a performed test that holds personal data. */
    SECURITY_TEST_PERSONAL_DATA_HELD: 'ai_security_test_personal_data_held',
    SECURITY_FINDING_NOT_FOUND: 'ai_security_finding_not_found',
    /** AC8 — a finding already closed, or changed after it was closed. */
    SECURITY_FINDING_CLOSED: 'ai_security_finding_closed',
    /** AC8 — a finding closed before anyone fixed it. */
    SECURITY_FINDING_NOT_FIXED: 'ai_security_finding_not_fixed',
    /** AC8 — a re-test dated on or before the fix. */
    SECURITY_RETEST_NOT_AFTER_FIX: 'ai_security_retest_not_after_fix',
    /** Cluster B, G8 — a finding closed, or opened, without its severity, owner, due date or change record. Returned as 400 with field_errors. */
    SECURITY_FINDING_FIELDS_REQUIRED: 'ai_security_finding_fields_required',
    /** Cluster B, G8 — a finding names a change record that is not a change of this system. */
    SECURITY_FINDING_CHANGE_RECORD_NOT_FOUND: 'ai_security_finding_change_record_not_found',
    /** Cluster B, G9 — `not_mandatory` on a category that is not one of the four mandatory for a high-risk version, or on a high-risk system. */
    SECURITY_STATE_NOT_FOR_CATEGORY: 'ai_security_state_not_for_category',
    SECURITY_NOT_MANDATORY_HIGH_RISK: 'ai_security_not_mandatory_high_risk',
    /** Cluster B, step 8 — a periodic schedule with a period that is not a whole number of months, or a last test date that is not a calendar day on or before today. */
    SECURITY_SCHEDULE_INVALID: 'ai_security_schedule_invalid',
    /** AC6, AC8, AC9 — a closure or re-test with no re-test date. */
    RETEST_DATE_REQUIRED: 'ai_governance_retest_date_required',
    /** AC8 — the re-tester is the person who fixed the finding, where the record requires separation. */
    SEPARATION_OF_DUTIES: 'ai_governance_separation_of_duties',
    /** AC9 — the validator is the owner or a listed developer of the version under test. */
    VALIDATOR_NOT_INDEPENDENT: 'ai_validation_validator_not_independent',
    VALIDATION_NOT_FOUND: 'ai_validation_not_found',
    VALIDATION_TESTS_REQUIRED: 'ai_validation_tests_required',
    VALIDATION_INDEPENDENCE_REQUIRED: 'ai_validation_independence_required',
    VALIDATION_FINDING_OWNER_AND_DUE_REQUIRED: 'ai_validation_finding_owner_and_due_required',
    VALIDATION_FINDING_RESULT_REQUIRED: 'ai_validation_finding_result_required',
    VALIDATION_FINDING_CLOSED: 'ai_validation_finding_closed',
    VALIDATION_SCOPE_REQUIRED: 'ai_validation_scope_required',
    VALIDATION_FINDING_NOT_FOUND: 'ai_validation_finding_not_found',
    /** A sign-off is made on an open validation only; a signed or stale one is not signed again. */
    VALIDATION_NOT_OPEN: 'ai_validation_not_open',
    /** The version the validation names has been superseded; a validation of the current version is opened instead. */
    VALIDATION_VERSION_SUPERSEDED: 'ai_validation_version_superseded',
    VALIDATION_FINDING_RESPONSE_REQUIRED: 'ai_validation_finding_response_required',
    /** AC1 — an inherited classification carries the supplier's reference (Điều 4 khoản 4). */
    INHERITED_CLASSIFICATION_REFERENCE_REQUIRED: 'ai_inherited_classification_reference_required',
    /** AC2 — a review that changed a profile says what changed. */
    INVENTORY_REVIEW_CHANGE_SUMMARY_REQUIRED: 'ai_inventory_review_change_summary_required',
    INVENTORY_REVIEW_OUTCOME_INVALID: 'ai_inventory_review_outcome_invalid',
    INVENTORY_REVIEW_ALREADY_CONFIRMED: 'ai_inventory_review_already_confirmed',
    /** Fix-sprint-20261031-1200 F25 — the bank-wide inventory export holds at most `AI_INVENTORY_EXPORT_MAX_ROWS` rows. */
    INVENTORY_EXPORT_TOO_LARGE: 'ai_inventory_export_too_large',
    /** AC5 — an impact assessment and its approval (Điều 13). */
    IMPACT_ASSESSMENT_NOT_FOUND: 'ai_impact_assessment_not_found',
    IMPACT_ASSESSMENT_OWNER_CANNOT_APPROVE: 'ai_impact_assessment_owner_cannot_approve',
    IMPACT_ASSESSMENT_ALREADY_APPROVED: 'ai_impact_assessment_already_approved',
    IMPACT_ASSESSMENT_DPIA_NOT_FOUND: 'ai_impact_assessment_dpia_not_found',
    IMPACT_ASSESSMENT_PERSONAL_DATA_REFERENCE_REQUIRED: 'ai_impact_assessment_personal_data_reference_required',
    /** AC7 — a release approval (Điều 12 khoản 3; Điều 22 khoản 1–2). */
    RELEASE_APPROVER_NOT_INDEPENDENT: 'ai_release_approver_not_independent',
    RELEASE_MISSING_ITEMS: 'ai_release_missing_items',
    RELEASE_GO_LIVE_DATE_REQUIRED: 'ai_release_go_live_date_required',
    RELEASE_ALREADY_RECORDED: 'ai_release_approval_already_recorded',
};
export const AI_PAYMENT_AUTHORITY_COLLECTION = 'grc_ai_payment_authorities';
export const AI_PAYMENT_AUTHORITY_EVENT_COLLECTION = 'grc_ai_payment_authority_events';
export const AI_PAYMENT_AUTHORITY_VERSION_COLLECTION = 'grc_ai_payment_authority_versions';
export const AI_INTERVENTION_COLLECTION = 'grc_ai_interventions';
export const AI_MONITORING_THRESHOLD_COLLECTION = 'grc_ai_monitoring_thresholds';
export const AI_MONITORING_COMPARISON_COLLECTION = 'grc_ai_monitoring_comparisons';
export const AI_MONITORING_EVENT_COLLECTION = 'grc_ai_monitoring_events';
export const AI_DUTY_HOLDER_COLLECTION = 'grc_ai_duty_holders';
export const AI_COMPETENCE_CAPACITY_COLLECTION = 'grc_ai_competence_capacity';
export const AI_INCIDENT_RECORD_COLLECTION = 'grc_ai_incident_records';
/** The deadline record that carries the competence-review interval declares this surface (Điều 21 khoản 4). */
export const AI_COMPETENCE_REGISTER_SURFACE = 'ai_competence_review';
/**
 * The elements an authority record must hold before it can be activated (Điều 5
 * khoản 1 g(i)–(iii)). A record missing any one reads `incomplete`.
 */
export const AI_PAYMENT_AUTHORITY_ELEMENTS = [
    'customer_class',
    'agreement_reference',
    'automated_transaction_scope',
    'per_transaction_limit_vnd',
    'per_day_limit_vnd',
    'valid_from',
    'valid_until',
    'suspension_conditions',
    'customer_termination_clause_reference',
    'identity_verification_statement',
    'pause_or_cancel_tool_reference',
];
export const AI_PAYMENT_AUTHORITY_EVENT_KINDS = [
    'activate',
    'suspend',
    'revoke',
    'implement',
    'execution_summary',
    'complaint_link',
    'relief_reliance',
];
/**
 * `pending_approval`: written, and no second person has approved the write yet.
 * `expired`: the validity end has passed (UC-VN-60 step 5), and no stop is recorded.
 * `not_evidenced`: a suspension or revocation whose implemented time no person has entered.
 */
export const AI_PAYMENT_AUTHORITY_STATES = [
    'incomplete',
    'pending_approval',
    'ready',
    'active',
    'expired',
    'suspended',
    'revoked',
    'not_evidenced',
];
/** A version is `pending` until a second person approves it, then `approved` and in force from that day. */
export const AI_PAYMENT_VERSION_STATUSES = ['pending', 'approved'];
/**
 * How the system's recorded autonomy level sits against an automated payment
 * authority (UC-VN-60 step 4). `not_recorded` until P-VN-38 records a level.
 */
export const AI_PAYMENT_AUTONOMY_READINGS = ['matches', 'mismatch', 'not_recorded'];
export const AI_PAYMENT_STOP_KINDS = ['suspension', 'revocation'];
/** Why an imported execution summary is a breach. Each is judged by the platform against the record. */
export const AI_PAYMENT_BREACH_REASONS = [
    'daily_limit_exceeded',
    'transaction_limit_exceeded',
    'after_revocation',
    'after_expiry',
    /** fix-sprint-20261018-2116 F7 — executed on or after the day an implemented suspension took effect. */
    'during_suspension',
    /** fix-sprint-20261018-2116 F7 — executed before the day the authority was activated (or it never was). */
    'before_activation',
];
// ── Transaction confirmation relief (Sprint 62.48.0 AC3, relief part; built after Sprint 63.33.0 AC14) ──
/**
 * What the authority record can show for a condition of a declared confirmation relief. A condition names the keys
 * its evidence rests on, and it reads `recorded` only when every key holds on the record. The platform judges no
 * transaction against a condition: each key is something a person recorded, or a state derived from what they did.
 *
 * - The authority elements: the element is held on the terms in force.
 * - `ai_execution_reference`: a person stated how the unit tells a transaction the system executed automatically from
 *   one a person executed.
 * - `automation_conditions_statement`: a person stated that the conditions under which the instrument permits
 *   automation hold for the system.
 * - `terms_approved`: a second person approved the terms in force.
 * - `ceiling_checked`: the per-day limit was checked against a ceiling an installed framework declares.
 * - `authority_active`: the authority is active: not suspended, revoked, expired or waiting for a stop's implemented time.
 */
export const AI_PAYMENT_RELIEF_EVIDENCE_KEYS = [
    ...AI_PAYMENT_AUTHORITY_ELEMENTS,
    'ai_execution_reference',
    'automation_conditions_statement',
    'terms_approved',
    'ceiling_checked',
    'authority_active',
];
/**
 * Where one authority stands on one relief.
 *
 * - `conditions_not_recorded`: at least one condition is not recorded. Reliance is refused (422).
 * - `conditions_recorded`: every condition is recorded; no person has recorded that the unit relies on the relief.
 * - `relied_on`: a person recorded reliance, and every condition is still recorded.
 * - `relied_on_conditions_no_longer_recorded`: a person recorded reliance, and a condition no longer holds on the record.
 */
export const AI_PAYMENT_RELIEF_READINGS = [
    'conditions_not_recorded',
    'conditions_recorded',
    'relied_on',
    'relied_on_conditions_no_longer_recorded',
];
/** The intervention kinds (Điều 3 khoản 5; Điều 5 khoản 2–3; Điều 9 khoản 2 a). */
export const AI_INTERVENTION_KINDS = [
    'content_change',
    'override',
    'pause',
    'stop',
    'emergency_stop',
    'manual_operation',
    'resume',
];
/**
 * The kinds that have a requested time, and so read `requested` until a person confirms it. The requested
 * time is the instant the platform records the request: a person does not type it (UC-VN-61 step 4).
 */
export const AI_INTERVENTION_TIMED_KINDS = [
    'pause',
    'stop',
    'emergency_stop',
    'manual_operation',
    'resume',
];
/** The kinds a `resume` may follow: a pause, a stop or an emergency stop (UC-VN-61 step 5). */
export const AI_INTERVENTION_RESUMABLE_KINDS = ['pause', 'stop', 'emergency_stop'];
/**
 * The kinds that act on one decision, and so carry its identifier (UC-VN-61 step 4). The others act on the
 * channel as a whole, and carry no decision identifier.
 */
export const AI_INTERVENTION_DECISION_KINDS = [
    'content_change',
    'override',
    'manual_operation',
];
/** The kinds an incident may be linked to: the activities the serious-incident record names (Điều 9 khoản 2 a). */
export const AI_INCIDENT_INTERVENTION_KINDS = ['emergency_stop', 'manual_operation'];
export const AI_INTERVENTION_READINGS = ['recorded', 'requested', 'confirmed'];
export const AI_CHANNEL_COLLECTION = 'grc_ai_channels';
export const AI_ESCALATION_COLLECTION = 'grc_ai_escalations';
/** Who an intervention is escalated to: the system owner for cause analysis, the second line for the board report (UC-VN-61 step 6). */
export const AI_ESCALATION_ADDRESSEES = ['owner', 'second_line'];
export const AI_STAFF_BRIEFING_COLLECTION = 'grc_ai_staff_briefings';
/** The monitoring event kinds a person records (Điều 6 khoản 2). */
export const AI_MONITORING_EVENT_KINDS = [
    'output_deviation',
    'quality_decline',
    'bias',
    'model_drift',
    'data_drift',
];
/** The kind a threshold crossing creates. A person records every other kind. */
export const AI_MONITORING_THRESHOLD_EVENT_KIND = 'threshold_crossing';
export const AI_MONITORING_SOURCES = ['entered', 'imported'];
export const AI_MONITORING_EVENT_READINGS = ['open', 'closed'];
/** The duties a named person holds against a system (Điều 5 khoản 3). */
export const AI_DUTY_KINDS = ['monitor', 'detect_deviation', 'intervene'];
export const AI_DUTY_TRAINING_READINGS = ['completed', 'not_trained'];
export const AI_COMPETENCE_READINGS = ['open', 'overdue', 'cadence_not_declared'];
/** Whether a system is built or run by the unit (Điều 21 khoản 1), or outsourced (Điều 21 khoản 2). */
export const AI_CAPACITY_BASES = ['unit_builds_or_runs', 'outsourced'];
/** The organisation's reporting bodies for a serious AI incident (Điều 9 khoản 2 c). */
export const AI_STATE_REPORT_AUTHORITIES = [
    'competent_state_authority',
    'state_bank_it_department',
];
/** Why the report deadline is null. The text states no period, so no clock is computed. */
export const AI_INCIDENT_REPORT_DEADLINE_REASON = 'instrument_states_no_period';
/**
 * The `trigger_event` an installed deadline record names when it is the period of the report this
 * record holds (the report to the authorities the record's `AI_STATE_REPORT_AUTHORITIES` name). A
 * record under this trigger that states no number is the framework saying it states no period.
 */
export const AI_INCIDENT_STATE_REPORT_TRIGGER_EVENT = 'serious_ai_incident';
/**
 * The `sla_type` values of serious AI incident reporting clocks an installed framework may declare
 * for its own report. They are separate duties: the incident AI section names the frameworks that
 * declare them and applies none of them to the report this record holds.
 */
export const AI_SERIOUS_INCIDENT_CLOCK_SLA_TYPES = [
    'ai_serious_incident_report',
    'serious_incident_preliminary_severe',
    'serious_incident_preliminary_other',
    'serious_incident_final_report',
];
/**
 * The decommissioning record a retirement needs (Điều 15 khoản 5): the documents
 * kept for audit, and how the data, the models and the configuration were
 * disposed of or isolated. Each statement is a person's; the platform holds it.
 */
export const AI_DECOMMISSIONING_FIELDS = [
    'documents_retained_for_audit',
    'data_disposition',
    'model_disposition',
    'configuration_disposition',
];
/** Error codes for wave 2 (Sprint 62.48.0): payment authority, oversight, monitoring, duty holders, AI incidents. */
export const AI_WAVE2_ERROR_CODES = {
    /** AC3 — the per-day limit exceeds the ceiling the installed pack declares (422). */
    PAYMENT_CEILING_EXCEEDED: 'ai_payment_ceiling_exceeded',
    PAYMENT_CEILING_NOT_DECLARED: 'ai_payment_ceiling_not_declared',
    PAYMENT_AUTHORITY_NOT_FOUND: 'ai_payment_authority_not_found',
    PAYMENT_AUTHORITY_INCOMPLETE: 'ai_payment_authority_incomplete',
    PAYMENT_AUTHORITY_EVENT_INVALID: 'ai_payment_authority_event_invalid',
    PAYMENT_STOP_NOT_FOUND: 'ai_payment_stop_not_found',
    PAYMENT_EXECUTION_DUPLICATE: 'ai_payment_execution_duplicate',
    PAYMENT_LEAD_NOT_FOUND: 'ai_payment_lead_not_found',
    /** UC-VN-60 criterion 5 and step 1 — a write is approved by a second person (403 for the writer). */
    PAYMENT_VERSION_NOT_FOUND: 'ai_payment_authority_version_not_found',
    PAYMENT_VERSION_PENDING: 'ai_payment_authority_version_pending',
    PAYMENT_VERSION_NOT_PENDING: 'ai_payment_authority_version_not_pending',
    PAYMENT_SELF_APPROVAL: 'ai_payment_authority_self_approval',
    PAYMENT_NOT_APPROVED: 'ai_payment_authority_not_approved',
    PAYMENT_AUTHORITY_EXPIRED: 'ai_payment_authority_expired',
    /** UC-VN-60 step 8 — a payment dispute links to a complaint about this system. */
    PAYMENT_COMPLAINT_NOT_FOUND: 'ai_payment_complaint_not_found',
    PAYMENT_COMPLAINT_NOT_FOR_SYSTEM: 'ai_payment_complaint_not_for_system',
    PAYMENT_COMPLAINT_ALREADY_LINKED: 'ai_payment_complaint_already_linked',
    /** AC3, relief part — reliance on a declared confirmation relief. */
    PAYMENT_RELIEF_NOT_DECLARED: 'ai_payment_relief_not_declared',
    PAYMENT_RELIEF_CONDITIONS_NOT_RECORDED: 'ai_payment_relief_conditions_not_recorded',
    PAYMENT_RELIEF_SELF_REVIEW: 'ai_payment_relief_self_review',
    /** fix-sprint-20261018-2116 F3 — another event on this authority was recorded at the same moment (409; re-read). */
    PAYMENT_AUTHORITY_EVENT_RACED: 'ai_payment_authority_event_raced',
    /** fix-sprint-20261018-2116 F6 — revocation is final: no stop is recorded on a revoked authority (409). */
    PAYMENT_AUTHORITY_REVOKED: 'ai_payment_authority_revoked',
    /** AC4 — interventions and decommissioning (Điều 3 khoản 5; Điều 15 khoản 5). */
    INTERVENTION_NOT_FOUND: 'ai_intervention_not_found',
    INTERVENTION_REASON_REQUIRED: 'ai_intervention_reason_required',
    INTERVENTION_TIME_REQUIRED: 'ai_intervention_time_required',
    INTERVENTION_CONFIRMATION_BEFORE_REQUEST: 'ai_intervention_confirmation_before_request',
    INTERVENTION_ALREADY_CONFIRMED: 'ai_intervention_already_confirmed',
    INTERVENTION_INCIDENT_NOT_LINKED: 'ai_intervention_incident_not_linked',
    DECOMMISSIONING_REQUIRED: 'ai_decommissioning_required',
    /** AC10 — monitoring (Điều 6 khoản 2; Điều 16 khoản 5). */
    MONITORING_OWNER_ONLY: 'ai_monitoring_thresholds_owner_only',
    MONITORING_EVENT_NOT_FOUND: 'ai_monitoring_event_not_found',
    MONITORING_EVENT_ALREADY_CLOSED: 'ai_monitoring_event_already_closed',
    /** AC18 — duty holders and competence (Điều 5 khoản 3; Điều 21). */
    DUTY_HOLDER_NOT_FOUND: 'ai_duty_holder_not_found',
    DUTY_USER_NOT_FOUND: 'ai_duty_user_not_found',
    DUTY_TRAINING_ASSIGNMENT_INVALID: 'ai_duty_training_assignment_invalid',
    DUTY_SUPPLIER_CAPACITY_REQUIRED: 'ai_duty_supplier_capacity_required',
    COMPETENCE_CAPACITY_INCOMPLETE: 'ai_competence_capacity_incomplete',
    /** AC17 — serious AI incidents (Điều 9). */
    INCIDENT_NOT_FOUND: 'ai_incident_not_found',
    INCIDENT_NOT_LINKED: 'ai_incident_not_linked',
    INCIDENT_NOT_SERIOUS: 'ai_incident_not_serious',
    INCIDENT_STATE_REPORT_DUPLICATE: 'ai_incident_state_report_duplicate',
    /** fix-sprint-20261018-2116 F3 — the record changed between the read and the write (409; re-read). */
    INCIDENT_RECORD_RACED: 'ai_incident_record_raced',
    /** Cluster D (UC-VN-61) — channels, the decision identifier, resume, and escalation responses. */
    INTERVENTION_CHANNEL_NOT_FOUND: 'ai_intervention_channel_not_found',
    INTERVENTION_DECISION_REQUIRED: 'ai_intervention_decision_required',
    INTERVENTION_RESUME_TARGET_INVALID: 'ai_intervention_resume_target_invalid',
    INTERVENTION_ALREADY_RESUMED: 'ai_intervention_already_resumed',
    /** Cluster D2 (UC-VN-61 step 5): a dual-control intervention is refused while no second person has approved it. */
    INTERVENTION_NOT_APPROVED: 'ai_intervention_not_approved',
    /** Cluster D2: the intervention is not waiting for approval (it needs none, or it is already approved). */
    INTERVENTION_NOT_PENDING_APPROVAL: 'ai_intervention_not_pending_approval',
    /** Cluster D2: the person who recorded an intervention cannot approve it (403). */
    INTERVENTION_SELF_APPROVAL: 'ai_intervention_self_approval',
    CHANNEL_NOT_FOUND: 'ai_channel_not_found',
    CHANNEL_NAME_REQUIRED: 'ai_channel_name_required',
    CHANNEL_NAME_TAKEN: 'ai_channel_name_taken',
    CHANNEL_SUPERVISOR_LINE_REQUIRED: 'ai_channel_supervisor_line_required',
    ESCALATION_NOT_FOUND: 'ai_escalation_not_found',
    ESCALATION_ALREADY_ANSWERED: 'ai_escalation_already_answered',
    ESCALATION_RESPONSE_REQUIRED: 'ai_escalation_response_required',
    ESCALATION_RESPONDER_NOT_PERMITTED: 'ai_escalation_responder_not_permitted',
    /** fix-sprint-20261031-1200 F18 — an owner escalation is answered by the system's owner. */
    ESCALATION_RESPONDER_NOT_OWNER: 'ai_escalation_responder_not_owner',
    /** fix-sprint-20261031-1200 F18 — the person who recorded the intervention does not answer its owner escalation. */
    ESCALATION_RESPONDER_IS_RECORDER: 'ai_escalation_responder_is_recorder',
};
/**
 * AC11 — the kinds of disclosure text. `pre_service_information` is the information
 * on use and limits given before the customer agrees (Điều 20 khoản 2);
 * `ai_interaction_statement` is the start-of-session statement that the customer is
 * dealing with an AI system (Điều 20 khoản 3); `internal_user_information` is the
 * guidance to internal users (Điều 11 khoản 1–2).
 */
export const AI_DISCLOSURE_KINDS = [
    'pre_service_information',
    'ai_interaction_statement',
    'internal_user_information',
];
/**
 * AC12 — the decision categories: `standard`, and the five restricted purposes (Điều 20 khoản 5, last sentence).
 * For a restricted one the customer is told a reason only; the basis is still stored.
 */
export const AI_COMPLAINT_DECISION_CATEGORIES = [
    'standard',
    'fraud',
    'scam',
    'money_laundering',
    'terrorism_financing',
    'cyber_security',
];
/**
 * AC12, AC13 — the restricted categories: the five purposes Điều 20 khoản 5's last sentence names, read from the text
 * (phòng chống gian lận, lừa đảo, rửa tiền, tài trợ khủng bố, bảo đảm an ninh mạng). The sentence relaxes only what is
 * NOTIFIED to the customer — a reason, fitted to the risk review and the safety of the customer's assets. It does not
 * relax the storage duty of the khoản's first sentence: the basis and main factors of such a decision are stored as
 * for any other.
 */
export const AI_RESTRICTED_DECISION_CATEGORIES = [
    'fraud',
    'scam',
    'money_laundering',
    'terrorism_financing',
    'cyber_security',
];
export const AI_COMPLAINT_STATUSES = ['open', 'routed', 'closed'];
/**
 * fix-sprint-20261031-1200 F8 — why the customer complained. ComplianceOne's own vocabulary: the Circular names none.
 * A new complaint names one; a complaint recorded before reads `not_recorded` (`AI_COMPLAINT_REASON_READINGS`).
 */
export const AI_COMPLAINT_REASONS = [
    'decision_outcome',
    'explanation',
    'unfair_treatment',
    'data_error',
    'ai_interaction',
    'service_failure',
    'other',
];
/** F8 — what a complaint's reason reads: one of the vocabulary, or `not_recorded` for a complaint stored before it. */
export const AI_COMPLAINT_REASON_READINGS = [...AI_COMPLAINT_REASONS, 'not_recorded'];
/**
 * F37 — when an explanation was delivered: the one recorded with the complaint (`at_intake`), or one the routed reviewer
 * recorded after the re-review and before closure (`after_re_review`, UC-VN-62 step 5).
 */
export const AI_COMPLAINT_EXPLANATION_STAGES = ['at_intake', 'after_re_review'];
/**
 * AC12 — why an item of a complaint was withheld from the customer. The first two are the categories Điều 20 khoản 5's
 * second sentence names, read from the text (mã nguồn, thuật toán chi tiết, bộ tham số; bí mật kinh doanh, bí mật
 * công nghệ); the others are the organisation's own vocabulary, not a legal reading.
 */
export const AI_WITHHELD_ITEM_CATEGORIES = [
    'source_code_algorithm_or_parameters',
    'business_or_technology_secret',
    'third_party_information',
    'security_sensitive',
    'restricted_decision',
    'other',
];
/** F8 — what the organisation did about a pattern of complaints. The Circular sets no response; ComplianceOne decides none. */
export const AI_COMPLAINT_PATTERN_RESPONSE_KINDS = [
    'no_change_with_reason',
    'revalidation_requested',
    'intervention_recorded',
    'incident_recorded',
    'change_recorded',
    'other',
];
/** F8 — the kinds that name a record of the system's: the response links it, and it must exist. */
export const AI_COMPLAINT_PATTERN_LINKED_KINDS = [
    'revalidation_requested',
    'intervention_recorded',
    'incident_recorded',
    'change_recorded',
];
/** AC13 — the effect a decision has on a person (Điều 20 khoản 5). */
export const AI_DECISION_EFFECTS = ['legal_or_financial_obligation', 'customer_access'];
/** AC13 — what the record holds for a decision's basis. */
export const AI_DECISION_BASIS_READINGS = [
    /** A standard decision whose basis is stored. */
    'recorded',
    /** Any decision, restricted or not, with no basis stored: the storage duty is the same for both. */
    'basis_not_retained',
    /** A restricted decision: its basis is stored, and the institution need only tell the customer the stated reason. */
    'reason_notified_basis_recorded',
];
/**
 * The eight minimum terms of Điều 18 khoản 3, in the pack's order of points
 * (a, b, c, d, đ, e, g, h). E12.5 compliance F1 (2026-10-31): the register held
 * six, and a contract with all six present read as compliant while two of the
 * pack's terms were never checked.
 */
export const AI_SUPPLIER_CLAUSE_KEYS = [
    'it_service_contract_rules',
    'assurance_evidence',
    'version_reporting',
    'monitoring_access',
    'technical_information_and_limits',
    'continuity_and_transition',
    'regulatory_cooperation',
    'training_data_commitment',
];
/** AC15 — the states a clause is entered in. A clause with no state reads `not_recorded`. */
export const AI_SUPPLIER_CLAUSE_STATES = ['present', 'absent', 'pending'];
/** AC15 — the training-data position: a commitment, a consent with its scope, or not recorded. */
export const AI_TRAINING_DATA_POSITIONS = ['commitment', 'consent', 'not_recorded'];
/**
 * Fix-sprint-20261031-1200 F4 — where an assurance report's warning period came from: the organisation's own figure,
 * or ComplianceOne's default ({@link AI_SUPPLIER_ASSURANCE_WARN_DAYS_DEFAULT}). No installed framework sets one.
 */
export const AI_SUPPLIER_WARN_DAYS_SOURCES = ['organisation', 'platform_default'];
/** Sprint 62.48.0, walk cluster F (G28–G32). The default number of days before expiry a report's pre-expiry task is due (UC-VN-63 timers: "bank-set", 30 days). */
export const AI_SUPPLIER_ASSURANCE_WARN_DAYS_DEFAULT = 30;
/**
 * Fix-sprint-20261031-1200 F25 — the most rows (recorded systems plus imported legacy entries) one bank-wide inventory
 * export holds. The export is counted before the systems are read, and refused above this (422
 * `ai_inventory_export_too_large`) rather than loading an unbounded register into memory. A platform limit, not a
 * regulatory one.
 */
export const AI_INVENTORY_EXPORT_MAX_ROWS = 10_000;
/**
 * Sprint 62.48.0, walk cluster F (G28–G32). The events a supplier arrangement holds, in the order they were recorded.
 * One route records every kind (`POST …/suppliers/:vendorId/version-reports`). Each carries `recorded_on`, the day the bank
 * recorded it, and `recorded_by`. Latest-wins readings (the continuity test, the monitoring review, the regulator contact,
 * the review) are read from the last event of their kind; nothing is overwritten.
 */
export const AI_SUPPLIER_EVENT_KINDS = [
    'model_version',
    'sub_processor_change',
    'termination',
    'regulator_request',
    'regulator_contact',
    'continuity_test',
    'monitoring_access_review',
    'review',
];
/** AC16 — the kinds of component (Điều 19 khoản 2). */
export const AI_COMPONENT_KINDS = ['library', 'model', 'dataset', 'dependency'];
/** AC19 — the data-use kinds of a provenance source (Điều 7 khoản 2). */
export const AI_PROVENANCE_DATA_USES = ['training', 'test', 'operating'];
/** Error codes for wave 3 (Sprint 62.48.0): fairness, disclosures, complaints, decisions, suppliers, components, provenance. */
export const AI_WAVE3_ERROR_CODES = {
    /** AC5 — the fairness section (Điều 17 khoản 2) and the reopening (Điều 13 khoản 2). */
    IMPACT_FAIRNESS_REQUIRED: 'ai_impact_fairness_required',
    IMPACT_APPROVED_LOCKED: 'ai_impact_assessment_approved_locked',
    IMPACT_FAIRNESS_INCOMPLETE: 'ai_impact_fairness_incomplete',
    /** AC11 — disclosures (Điều 11; Điều 20 khoản 2–3). */
    DISCLOSURE_CONTENT_REQUIRED: 'ai_disclosure_content_required',
    DISCLOSURE_GO_LIVE_CONFLICT: 'ai_disclosure_go_live_conflict',
    DISCLOSURE_DATE_INVALID: 'ai_disclosure_date_invalid',
    /** Cluster D (G19, G23) — a disclosure names a channel record; a same-day version gives its reason. */
    DISCLOSURE_CHANNEL_NOT_FOUND: 'ai_disclosure_channel_not_found',
    DISCLOSURE_SAME_DAY_REASON_REQUIRED: 'ai_disclosure_same_day_reason_required',
    /** Cluster D2 (UC-VN-61 step 2): the version is not waiting for approval (it is already in force). */
    DISCLOSURE_NOT_PENDING: 'ai_disclosure_not_pending',
    /** Cluster D2: the person who recorded a disclosure version cannot approve it (403). */
    DISCLOSURE_SELF_APPROVAL: 'ai_disclosure_self_approval',
    DISCLOSURE_NOT_FOUND: 'ai_disclosure_not_found',
    /** Cluster D (G20) — a staff briefing on a system's limits. */
    BRIEFING_DATE_INVALID: 'ai_staff_briefing_date_invalid',
    BRIEFING_CONTENT_REQUIRED: 'ai_staff_briefing_content_required',
    BRIEFING_PROCEDURE_VERSION_REQUIRED: 'ai_staff_briefing_procedure_version_required',
    BRIEFING_ATTENDEES_REQUIRED: 'ai_staff_briefing_attendees_required',
    BRIEFING_ATTENDEE_UNKNOWN: 'ai_staff_briefing_attendee_unknown',
    /** AC12 — complaints and human re-review (Điều 20 khoản 4–5). */
    COMPLAINT_NOT_FOUND: 'ai_complaint_not_found',
    /** fix-sprint-20261018-2116 F4 — the system named and the system of the decision named are not the same (400). */
    COMPLAINT_SYSTEM_MISMATCH: 'ai_complaint_system_mismatch',
    /** E12.5 code E-1 (2026-10-31) — the complaint's category is not the linked decision's (400). */
    COMPLAINT_CATEGORY_MISMATCH: 'ai_complaint_category_mismatch',
    /** fix-sprint-20261018-2116 L2 — the complaint changed between the read and the write (409; re-read). */
    COMPLAINT_RACED: 'ai_complaint_raced',
    COMPLAINT_REVIEWER_NOT_FOUND: 'ai_complaint_reviewer_not_found',
    COMPLAINT_REVIEWER_NOT_INDEPENDENT: 'ai_complaint_reviewer_not_independent',
    COMPLAINT_REVIEWER_NOT_ROUTED: 'ai_complaint_reviewer_not_routed',
    COMPLAINT_NOT_ROUTED: 'ai_complaint_not_routed',
    COMPLAINT_RE_REVIEW_REQUIRED: 'ai_complaint_re_review_required',
    COMPLAINT_ALREADY_RE_REVIEWED: 'ai_complaint_already_re_reviewed',
    COMPLAINT_ALREADY_CLOSED: 'ai_complaint_already_closed',
    COMPLAINT_OUTCOME_REQUIRED: 'ai_complaint_re_review_outcome_required',
    COMPLAINT_REASON_REQUIRED: 'ai_complaint_stated_reason_required',
    COMPLAINT_THRESHOLD_INVALID: 'ai_complaint_threshold_invalid',
    COMPLAINT_DATE_INVALID: 'ai_complaint_date_invalid',
    COMPLAINT_WITHHELD_CATEGORY_INVALID: 'ai_complaint_withheld_category_invalid',
    /** Sprint 62.48.0, walk cluster E (G25, G26) — the explanation's delivery day and channel; the customer notification at closure. */
    COMPLAINT_EXPLANATION_DELIVERY_REQUIRED: 'ai_complaint_explanation_delivery_required',
    COMPLAINT_NOTIFICATION_REQUIRED: 'ai_complaint_customer_notification_required',
    /** fix-sprint-20261031-1200 F8 — a new complaint names its reason from the ComplianceOne vocabulary (400). */
    COMPLAINT_REASON_INVALID: 'ai_complaint_reason_invalid',
    /** F8 — a counts period is `YYYY-MM` to `YYYY-MM`, the first not after the second (400). */
    COMPLAINT_PERIOD_INVALID: 'ai_complaint_period_invalid',
    /** F8 — the pattern-response record: who answers, what it names, and what it links. */
    COMPLAINT_PATTERN_RESPONDER_NOT_ADDRESSEE: 'ai_complaint_pattern_responder_not_addressee',
    COMPLAINT_PATTERN_NOT_CROSSED: 'ai_complaint_pattern_not_crossed',
    COMPLAINT_PATTERN_LINK_REQUIRED: 'ai_complaint_pattern_link_required',
    COMPLAINT_PATTERN_LINK_NOT_FOUND: 'ai_complaint_pattern_link_not_found',
    COMPLAINT_PATTERN_RESPONSE_INVALID: 'ai_complaint_pattern_response_invalid',
    /** Gate E12.5 CR-P3-6 — the crossing already has its response; a second is refused (409), never stacked. */
    COMPLAINT_PATTERN_ALREADY_ANSWERED: 'ai_complaint_pattern_already_answered',
    /** F37 — an explanation after the re-review: its text, day and channel (400). */
    COMPLAINT_EXPLANATION_INVALID: 'ai_complaint_explanation_invalid',
    /** AC13 — decision basis and explanation (Điều 20 khoản 5). */
    DECISION_REASON_REQUIRED: 'ai_decision_stated_reason_required',
    DECISION_DATE_INVALID: 'ai_decision_date_invalid',
    DECISION_EXPLANATION_INVALID: 'ai_decision_explanation_invalid',
    /** AC15 — third-party AI supplier arrangements (Điều 18). */
    SUPPLIER_VENDOR_NOT_FOUND: 'ai_supplier_vendor_not_found',
    SUPPLIER_SYSTEM_NOT_FOUND: 'ai_supplier_system_not_found',
    SUPPLIER_SYSTEM_IN_HOUSE: 'ai_supplier_system_in_house',
    SUPPLIER_CLAUSE_REFERENCE_REQUIRED: 'ai_supplier_clause_reference_required',
    SUPPLIER_TRAINING_SCOPE_REQUIRED: 'ai_supplier_training_scope_required',
    SUPPLIER_ARRANGEMENT_NOT_FOUND: 'ai_supplier_arrangement_not_found',
    SUPPLIER_PERIOD_INVALID: 'ai_supplier_period_invalid',
    /** Walk cluster F (G28–G32) — an event's day is a calendar day; a termination or a further version report is refused once terminated. */
    SUPPLIER_EVENT_DATE_INVALID: 'ai_supplier_event_date_invalid',
    SUPPLIER_EVENT_FIELD_REQUIRED: 'ai_supplier_event_field_required',
    SUPPLIER_ARRANGEMENT_TERMINATED: 'ai_supplier_arrangement_terminated',
    /** Fix-sprint-20261031-1200 F35 — a consent names its written consent and the organisation's basis for it (400). */
    SUPPLIER_TRAINING_CONSENT_REFERENCE_REQUIRED: 'ai_supplier_training_consent_reference_required',
    SUPPLIER_TRAINING_CONSENT_BASIS_REQUIRED: 'ai_supplier_training_consent_basis_required',
    /** Fix-sprint-20261031-1200 F35 — a commitment cannot be recorded while the training-data clause reads absent (400). */
    SUPPLIER_TRAINING_POSITION_CONFLICT: 'ai_supplier_training_position_conflict',
    /** Fix-sprint-20261031-1200 F7 — the supplier record export names its period (400). */
    SUPPLIER_EXPORT_PERIOD_REQUIRED: 'ai_supplier_export_period_required',
    /** AC16 — component register and model file integrity (Điều 19). */
    COMPONENT_NOT_FOUND: 'ai_component_not_found',
    COMPONENT_CHECK_VALUE_REQUIRED: 'ai_component_check_value_required',
    COMPONENT_INTEGRITY_INCOMPLETE: 'ai_component_integrity_incomplete',
    ADVISORY_DECISION_REQUIRED: 'ai_component_advisory_decision_required',
    SUPPLY_CHAIN_SUMMARY_REQUIRED: 'ai_supply_chain_review_summary_required',
    /** AC19 — data provenance and operational logs (Điều 7; Điều 10). */
    PROVENANCE_SYNTHETIC_ASSESSMENT_REQUIRED: 'ai_provenance_synthetic_assessment_required',
    PROVENANCE_RETENTION_CITATION_REQUIRED: 'ai_provenance_retention_citation_required',
    PROVENANCE_RETENTION_PERIOD_REQUIRED: 'ai_provenance_retention_period_required',
};
// ── Wave 3 collections and register surface ───────────────────────
export const AI_DISCLOSURE_COLLECTION = 'grc_ai_disclosures';
export const AI_COMPLAINT_COLLECTION = 'grc_ai_complaints';
export const AI_COMPLAINT_THRESHOLD_COLLECTION = 'grc_ai_complaint_thresholds';
/** fix-sprint-20261031-1200 F8 — the organisation's responses to a pattern of complaints. */
export const AI_COMPLAINT_PATTERN_RESPONSE_COLLECTION = 'grc_ai_complaint_pattern_responses';
export const AI_DECISION_RECORD_COLLECTION = 'grc_ai_decision_records';
export const AI_SUPPLIER_ARRANGEMENT_COLLECTION = 'grc_ai_supplier_arrangements';
export const AI_COMPONENT_SNAPSHOT_COLLECTION = 'grc_ai_components';
export const AI_COMPONENT_INTEGRITY_COLLECTION = 'grc_ai_component_integrity';
export const AI_COMPONENT_ADVISORY_COLLECTION = 'grc_ai_component_advisories';
export const AI_SUPPLY_CHAIN_REVIEW_COLLECTION = 'grc_ai_supply_chain_reviews';
export const AI_DATA_PROVENANCE_COLLECTION = 'grc_ai_data_provenance';
/** AC16 — the deadline record that carries the supply-chain review's interval (Điều 19 khoản 5). Read by surface, never by instrument name. */
export const AI_SUPPLY_CHAIN_REGISTER_SURFACE = 'ai_supply_chain_review';
// ── AI risk-management framework, three lines and board figures (Sprint 62.48.0, wave 4, AC14) ──
/** The collection of AI risk-management framework records (Điều 12 khoản 2 b; Điều 14). */
export const AI_RISK_FRAMEWORK_COLLECTION = 'grc_ai_risk_framework_records';
/** The collection of three-lines maps (UC-VN-64 criterion 4; Điều 14 khoản 2). */
export const AI_THREE_LINES_COLLECTION = 'grc_ai_three_lines_maps';
/** The three lines of defence (Điều 14 khoản 2). The second line names P-VN-42 or P-VN-07 (owner answer G2). */
export const AI_THREE_LINE_CODES = ['first', 'second', 'third'];
export const AI_THREE_LINE_SECOND_PERSONAS = ['P-VN-42', 'P-VN-07'];
/**
 * Fix-sprint-20261031-1200 F6 — one line's reading: `attested` by one of its own holders (on this version or carried
 * forward), `not_attested`, or `attested_by_non_holder` (a legacy attestation by someone not named on the line, which
 * counts as incomplete until a holder re-attests).
 */
export const AI_THREE_LINE_READINGS = [
    'attested',
    'not_attested',
    'attested_by_non_holder',
];
/**
 * fix-sprint-20261031-1200 F1 — what keeps the periodic report's figures partial: each is a thing the figures depend
 * on that is not recorded. `content_unreadable` names the content (`content`) whose register could not be read.
 * `record_not_installed` (gate E12.5 CR-P2-1): no installed deadline record declares the report's surface, so its scope,
 * contents and interval are not known; the figures then count nothing and must not read as having no gaps.
 */
export const AI_PERIODIC_REPORT_GAP_KINDS = [
    'record_not_installed',
    'unclassified_systems',
    'significance_not_recorded',
    'operation_date_not_recorded',
    'content_unreadable',
];
export const AI_DATED_OBLIGATION_DATE_REASONS = [
    'stated',
    'unit_holds_tier',
    'unit_condition_undetermined',
];
/**
 * Fix sprint #602 — why a system takes the date it does for one dated obligation, or why it takes none.
 *
 * - `every_system` — the stage dates every system alike.
 * - `existing_system` — the stage is for systems in operation before its cut-off, and this one is.
 * - `existing_system_moved` — a stage for existing systems replaces this obligation's date, and this system is one.
 * - `existing_system_partly_moved` — a stage for existing systems moves part of this obligation's provisions to its
 *   own date; the stated date stands for the rest, and this system is an existing one.
 * - `declaration_unreadable` — the stage that would decide this row cannot be read; no date is invented.
 * - `new_system` — the system went into operation on or after the cut-off, so the stated date stands.
 * - `unit_holds_tier` — the stage's unit condition holds: the organisation has a system of the named tier.
 * - `unit_condition_not_held` — the stage carries a unit condition the organisation does not meet (no system of the
 *   tier it names is recorded, and every system of the organisation is classified), so the stated date stands for
 *   every system. Never "every system" alone: the stage excepts a unit, and the row says this unit is not the excepted
 *   one.
 * - `unit_condition_undetermined` — the stage carries a unit condition no classified system meets, and systems of the
 *   organisation are not yet classified: whether the condition holds depends on their classification, so no date is
 *   stated ({@link AiSystemDatedObligation.unit_condition_undetermined} says what it depends on).
 * - `existing_systems_only` — the stage is for existing systems, and this system is not one.
 * - `new_systems_only` — the stage is for systems put into operation on or after its cut-off, and this one was in
 *   operation before it.
 * - `new_system_in_stated_period` — the stage is for systems put into operation on or after its cut-off within a
 *   period the provision states, and this one went into operation on or after the cut-off: the stated date, if that
 *   was within the period. The platform does not count the period.
 * - `operation_start_not_recorded` — the date depends on when the system went into operation, and nobody recorded it.
 * - `every_system_in_part` — a stage for existing systems moves part of this obligation's provisions, nobody recorded
 *   when the system went into operation, and the obligation's own stage names the part the move leaves at its date for
 *   every system (`every_system_part`): the stated date, for that part ({@link AiSystemDatedObligation.every_system_part});
 *   for the rest the date depends on the operation date.
 *
 * - `applies_through_condition_met` (fix-sprint-20261031-1200 F39(e)) — a provision a stage brings in through another
 *   (`applies_through`) reaches this existing system, which meets its condition: the declaration's date.
 * - `applies_through_condition_not_met` — this existing system does not meet the condition: the date of the stage
 *   that moves the provision for existing systems.
 * - `applies_through_condition_not_recorded` — whether it meets the condition is not recorded: no date is settled, and
 *   both dates are given ({@link AiSystemDatedObligation.through}).
 *
 * (`stated_for_named_systems`, read from a deadline's `applies_to`, is gone: the installer never persists that key,
 * so the reason could not arise on an installed record. Whom a dated rule reaches is declared on its stage.)
 */
export const AI_SYSTEM_DATED_OBLIGATION_REASONS = [
    'every_system',
    'unit_condition_not_held',
    'unit_condition_undetermined',
    'existing_system',
    'existing_system_moved',
    'existing_system_partly_moved',
    'new_system',
    'unit_holds_tier',
    'existing_systems_only',
    'new_systems_only',
    'new_system_in_stated_period',
    'operation_start_not_recorded',
    'every_system_in_part',
    'declaration_unreadable',
    'applies_through_condition_met',
    'applies_through_condition_not_met',
    'applies_through_condition_not_recorded',
];
/**
 * fix-sprint-20261031-1200 F15 — whether a read served beside a record was made. `unavailable` says it failed: its rows
 * are empty and its absence statement is null, and neither may be read as "none declared".
 */
export const AI_READ_STATES = ['available', 'unavailable'];
//# sourceMappingURL=ai-governance.js.map